import {
    Box,
    Chip,
    Grid,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mzc-pdc/ui";
import WidgetHead from "@components_v2/dashboard/ui/widget-head";
import theme from "@styles/theme";
import { useCallback, useEffect, useMemo, useState } from "react";
import WidgetWrap from "@components_v2/dashboard/ui/widget-wrapper";
import fp from "lodash/fp";
import { useDispatch, useSelector } from "react-redux";
import { getWidgetData } from "@modules/dashboard";
import { useTranslation } from "react-i18next";
import { NoSearchResultsIcon } from "@features/no-search-results-icon";
import TableCard from "@components_v2/card/table-card";
import convertUtcToLocal from "@cores/convertUtcToLocal";
import StatusBadge from "@components/statusbadge";
import formatDuration from "@cores/formatDuration";
import { useHistory } from "react-router-dom";
import AssetTypeFlag from "@components_v2/icon-type/asset/asset-type-flag";
import { IconArrowDownload, IconArrowUpload, IconSwapHorizCircle, ImageEmpty } from "@mzc-cloudplex/icons";
import { sessionKeeper } from "@cores/sessionKeeper";
import { setProject } from "@modules/project";
import menuService from "@services/menuService";
import dayjs from "dayjs";
import convertToByte from "@cores/convertToByte";
import { alpha } from "@mui/material";
import compareVersions, { VERSION_CM_14830_SEPARATION_OF_OWNER_AND_CREATOR } from "@cores/version";
import { addCommaToBigInt } from "@utils/convert-string";

export const WidgetTable = ({
    type,
    id,
    name,
    title,
    icon,
    color,
    onMove,
    resourceType,
    onClickRow,
    onDelete,
    fullWidth,
    columns,
    isNotice,
    link,
    filter,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();

    const projectId = useSelector(({ project }) => project.id);
    const dashboard = useSelector(({ dashboard }) => dashboard);
    const stage = useSelector(({ stage }) => stage);

    const defaultSelectedOption = { label: `All Users`, value: `ALL` };
    const [selectedOption, setSelectedOption] = useState(defaultSelectedOption);
    const [options, setOptions] = useState([]);

    const widgetData = useMemo(() => {
        return dashboard?.dashboardWidgetData?.find((el) => el.id === id);
    }, [dashboard]);

    const user = useMemo(() => {
        return stage?.user;
    }, [stage]);

    useEffect(() => {
        if (!user) {
            setOptions([{ label: `All Users`, value: "ALL" }]);
        } else
            setOptions([
                { label: `All Users`, value: "ALL" },
                { label: `Only mine`, value: fp.get(`id`, user) },
            ]);
    }, [stage]);

    const getWidgetDetail = useCallback(
        (options) => {
            const params = { limit: 40, ...options };
            dispatch(getWidgetData({ id, params }));
        },
        [stage],
    );

    const handleReset = useCallback(
        ({ selectedOption, projectId }) => {
            let params = {};
            if (selectedOption.value !== "ALL") params.createdById = selectedOption.value;
            getWidgetDetail(params);
        },
        [selectedOption, projectId],
    );

    const onChangeFilter = (value) => {
        const option = options.find((el) => el.value === value);
        setSelectedOption(option);
        handleReset({ selectedOption: option, projectId: projectId });
    };

    const parseStatus = useCallback(
        (columnId, value) => {
            if (columnId === "status") return value.toUpperCase();
            else if (columnId === "isActive") return value ? "ACTIVE" : "INACTIVE";
            return;
        },
        [dashboard],
    );

    const parseOwner = useCallback(
        (columnId, value) => {
            if (
                type === "RECENT_ASSETS" &&
                compareVersions(stage.version, VERSION_CM_14830_SEPARATION_OF_OWNER_AND_CREATOR) >= 0 &&
                columnId === "createdBy"
            )
                return value.owner?.name ?? "-";

            if (columnId === "owner") return value.createdBy?.name ?? "-";
            else if (columnId === "ownerName") return value.ownerName ?? "-";
            else if (columnId === "grantedBy") return value.granted?.by?.name ?? "-";
            else if (columnId === "createdBy") return value.createdBy?.name ?? "-";
            return "-";
        },
        [stage, dashboard],
    );

    const getAssetNameIcon = useCallback(
        (mediaType) => {
            return <AssetTypeFlag minWidth={22} width={22} height={16} type={mediaType} />;
        },
        [dashboard],
    );

    const handleChangeProject = useCallback(
        (prj) => {
            if (prj.id === projectId) sessionKeeper.set({ stage, prj });
            const sessionCookie = sessionKeeper.get();
            dispatch(setProject(prj, sessionCookie.authProvider));
            history.push(`${menuService.paths.dashboard(stage.id, fp.get(`id`, prj))}`);
        },
        [dispatch, history, stage],
    );

    const getStatisticDataByType = useCallback((data) => {
        switch (data.statistics.key) {
            case "TOTAL_SIZE":
                return convertToByte(data.statistics.value);
            case "TOTAL_COUNT":
                return addCommaToBigInt(data.statistics.value);
            default:
                return data.statistics.value;
        }
    }, []);

    const getValueByColumn = useCallback(
        ({ id: columnId, style: _columnStyle }, data) => {
            if (["name", "sharedAssetName"].includes(columnId)) {
                const domain = columnId === "sharedAssetName" ? data.domain : resourceType;
                const resource = columnId === "sharedAssetName" ? { ...data, ...data.resource } : data;
                const mediaType = columnId === "sharedAssetName" ? data.resource?.mediaType : data.mediaType;
                return (
                    <TableCard
                        name={{
                            name: (
                                <Typography variant={"body1_500"} component={"span"} noWrap>
                                    {resource.name}
                                </Typography>
                            ),
                            startIcon: domain === "ASSET" ? getAssetNameIcon(mediaType) : null,
                        }}
                    />
                );
            }
            if (["createdAt"].includes(columnId))
                return (
                    <Grid>
                        <Typography variant={"body1_400"} component={"span"}>
                            {data[columnId] ? convertUtcToLocal(data[columnId], "YYYY-MM-DD HH:mm:ss") : "-"}
                        </Typography>
                    </Grid>
                );
            if (["granted"].includes(columnId))
                return (
                    <Grid item>
                        {data[columnId].at ? `${convertUtcToLocal(data[columnId].at, "YYYY-MM-DD HH:mm:ss")} ` : "-"}
                    </Grid>
                );
            if (["ownerName", "owner", "grantedBy", "createdBy"].includes(columnId))
                return <Grid item>{parseOwner(columnId, data)}</Grid>;
            if (["isActive", "status"].includes(columnId))
                return (
                    <Grid item>
                        <StatusBadge status={parseStatus(columnId, data[columnId])} />
                    </Grid>
                );
            if (columnId === "duration")
                return (
                    <Grid>
                        <Typography variant={"body1_400"} component={"span"}>
                            {formatDuration(data.duration * 1000) ?? "-"}
                        </Typography>
                    </Grid>
                );
            if (columnId === "access")
                return (
                    <Chip
                        size={"small"}
                        icon={
                            <IconSwapHorizCircle
                                size={16}
                                color={
                                    projectId === data.id
                                        ? theme.palette.common.white
                                        : theme.palette.greySecondary[500]
                                }
                            />
                        }
                        color={projectId === data.id ? "primary" : "greySecondary"}
                        variant={projectId === data.id ? "filled" : "outlined"}
                        label={projectId === data.id ? t("common::label::Accessed") : t("common::label::Access")}
                        onClick={() => handleChangeProject(data)}
                        sx={{
                            bgcolor: projectId === data.id ? theme.palette.primary.light : undefined,
                            color:
                                projectId === data.id ? theme.palette.constant.white : theme.palette.greySecondary[500],
                        }}
                    />
                );
            if (columnId === "project")
                return (
                    <Grid item>
                        <Typography variant={"body1_400"} component={"span"}>
                            {data.project?.name ?? "-"}
                        </Typography>
                    </Grid>
                );
            if (columnId === "range") {
                const [start, end] = data[columnId].split("~");
                return (
                    <Grid item>
                        <Typography variant={"body1_400"} component={"span"}>
                            {start && end
                                ? `${dayjs(start).format("YYYY-MM-DD")}~${dayjs(end).format("YYYY-MM-DD")}`
                                : "-"}
                        </Typography>
                    </Grid>
                );
            }
            if (["totalAssetCount", "totalSize"].includes(columnId)) {
                return (
                    <Grid item>
                        <Typography variant={"body1_400"} component={"span"}>
                            {getStatisticDataByType(data)}
                        </Typography>
                    </Grid>
                );
            }
            if (columnId === "weeklyChange") {
                const weeklyChangeLabel =
                    BigInt(data.statistics.weeklyChange) < 0
                        ? BigInt(data.statistics.weeklyChange) * BigInt(-1)
                        : BigInt(data.statistics.weeklyChange);

                return (
                    <Box sx={{ display: "flex", alignItems: "flex-end" }}>
                        {BigInt(data.statistics.weeklyChange) < 0 ? (
                            <IconArrowDownload color={"error"} size={16} sx={{ mb: "2px" }} />
                        ) : (
                            <IconArrowUpload color={"success"} size={16} sx={{ mb: "2px" }} />
                        )}
                        <Typography
                            variant={"body1_400"}
                            sx={{
                                color:
                                    BigInt(data.statistics.weeklyChange) < 0
                                        ? theme.palette.error.main
                                        : theme.palette.success.main,
                            }}
                        >
                            {data.statistics.key === "TOTAL_SIZE"
                                ? convertToByte(weeklyChangeLabel)
                                : addCommaToBigInt(weeklyChangeLabel)}
                        </Typography>
                    </Box>
                );
            }
            return "-";
        },
        [widgetData],
    );

    useEffect(() => {
        getWidgetDetail();
    }, []);

    return (
        <WidgetWrap id={name} onMove={onMove} fullWidth={fullWidth} blockDrag={fullWidth} isNotice={isNotice}>
            <Grid container flexDirection={"column"} flex={1} height={"100%"}>
                <Grid item sx={{ borderBottom: filter ? `1px solid ${theme.palette.greySecondary[100]}` : 0 }}>
                    <WidgetHead
                        onRefresh={() => handleReset({ selectedOption })}
                        color={color}
                        icon={icon}
                        title={title}
                        link={link}
                        actionMenus={[
                            {
                                label: t(`common::label::Remove`, "Remove"),
                                onClick: (e) => {
                                    onDelete(name);
                                },
                                disabled: true, //!removable,
                            },
                        ]}
                    />
                </Grid>
                {filter && (
                    <Grid container flexDirection={"row"} py={1} px={2} justifyContent={"flex-end"}>
                        <Select
                            size="small"
                            sx={{ width: 140 }}
                            defaultValue={fp.get(`value`, selectedOption)}
                            value={fp.get(`value`, selectedOption)}
                            onChange={(e) => onChangeFilter(e.target.value)}
                        >
                            {options.map((option) => (
                                <MenuItem key={`widget-table-${option.value}`} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </Grid>
                )}
                <Grid item flex={1} sx={{ overflowY: "auto", width: "inherit" }}>
                    <TableContainer
                        sx={{
                            height: "100%",
                            "&::-webkit-scrollbar-track": {
                                borderLeft: `1px solid ${theme.palette.divider}`,
                                borderTop: `1px solid ${theme.palette.divider}`,
                            },
                        }}
                    >
                        {widgetData &&
                            (widgetData.pending ? (
                                <Grid container height={"100%"} justifyContent={"center"} alignItems={"center"}>
                                    <span className={"loading"}>
                                        <i className={"loading-content"} />
                                    </span>
                                </Grid>
                            ) : !widgetData?.pending && (widgetData?.data ?? []).length === 0 ? (
                                <NoSearchResultsIcon
                                    title={"There is no data for this widget."}
                                    icon={<ImageEmpty width={80} height={72} sx={{ marginBottom: 2.5 }} />}
                                />
                            ) : (
                                <Table stickyHeader shadowHeader borderHeader>
                                    <TableHead sx={{ height: theme.spacing(4.25), position: "relative", zIndex: 1 }}>
                                        <TableRow
                                            sx={{
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderBottom: 0,
                                            }}
                                        >
                                            {columns?.map((column, index) => (
                                                <TableCell
                                                    component="th"
                                                    sx={{ backgroundColor: `${theme.palette.grey[50]} !important` }}
                                                    key={`widget-table-${title}-${column.label}`}
                                                >
                                                    <Typography
                                                        variant={"body1"}
                                                        component={"p"}
                                                        required={column.required}
                                                    >
                                                        {column.label && t(`common::label::${column.label}`)}
                                                    </Typography>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {widgetData?.data?.map((option, index) => (
                                            <TableRow
                                                key={`widget-table-row-${title}-${option.id}`}
                                                sx={{
                                                    cursor: onClickRow ? "pointer" : "default",
                                                    "&:hover": {
                                                        backgroundColor: alpha(theme.palette.greySecondary[50], 0.24),
                                                    },
                                                }}
                                                id={option.id}
                                                tabIndex={-1}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onClickRow &&
                                                        history.push(
                                                            onClickRow(
                                                                name === "Recent Assets Shared To Me"
                                                                    ? option.resource?.id
                                                                    : option.id,
                                                                name === "Recent Assets Shared To Me"
                                                                    ? option.project.id
                                                                    : null,
                                                            ),
                                                        );
                                                }}
                                            >
                                                {columns.map((column) => (
                                                    <TableCell
                                                        key={`widget-table-row-${title}-${option.id}-${column.id}`}
                                                        style={{
                                                            ...column.style?.custom,
                                                        }}
                                                    >
                                                        {getValueByColumn(column, option)}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ))}
                    </TableContainer>
                </Grid>
            </Grid>
        </WidgetWrap>
    );
};
